import type { Application, Graphics } from 'pixi.js';
import type { World, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	AmmoComponent,
	CameraResource,
	EnemyComponent,
	ProjectileComponent,
	StageBounds,
	TimeScaleResource,
	TransformComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';

export type SpawnProjectileFn = (
	world: World,
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	projectiles: ComponentStore<ProjectileComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	vx: number,
	vy: number,
	options?: {
		fromEnemy?: boolean;
		lifetime?: number;
		size?: number;
		fill?: number;
		stroke?: number;
	}
) => void;

export type ApplyTimeBoostFn = (world: World, value: number, duration: number) => void;

export type PlayerFireOptions = {
	boundsKey: string;
	stageKey: string;
	playerFireLeadMaxTime: number;
	playerProjectileColor: number;
	playerProjectileStroke: number;
};

export function handlePlayerFire(
	world: World,
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	projectiles: ComponentStore<ProjectileComponent>,
	graphics: ComponentStore<Graphics>,
	enemies: ComponentStore<EnemyComponent>,
	ammos: ComponentStore<AmmoComponent>,
	spawnProjectile: SpawnProjectileFn,
	applyTimeBoost: ApplyTimeBoostFn,
	options: PlayerFireOptions
) {
	const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
	const playerId = world.getResource<number>('player-entity');
	const camera = world.getResource<CameraResource>('camera');
	const bounds = world.getResource<StageBounds>(options.boundsKey);
	const stage = world.getResource<Application['stage']>(options.stageKey);
	if (!playerId || !camera || !bounds || !stage) return;
	const playerTransform = transforms.get(playerId);
	if (!playerTransform) return;

	const ammo = ammos.get(playerId);
	if (!ammo) return;
	if (ammo.reloadRemaining > 0) return;
	if (ammo.remaining <= 0) {
		ammo.reloadRemaining = Math.max(0.05, ammo.reloadDuration);
		return;
	}

	const targetId = findNearestVisibleEnemy(enemies, transforms, camera, bounds, playerTransform);
	if (targetId === null) return;
	const targetTransform = transforms.get(targetId);
	const targetEnemy = enemies.get(targetId);
	if (!targetTransform || !targetEnemy) return;

	// Predictive aim: shoot at intercept point based on enemy velocity and projectile speed.
	const aim = computeInterceptAim(
		playerTransform.x,
		playerTransform.y,
		targetTransform.x,
		targetTransform.y,
		targetEnemy.vx ?? 0,
		targetEnemy.vy ?? 0,
		Math.max(1e-3, tuning.playerProjectileSpeed),
		options.playerFireLeadMaxTime
	);
	const dirX = aim.x - playerTransform.x;
	const dirY = aim.y - playerTransform.y;
	const len = Math.hypot(dirX, dirY) || 1;
	const vx = (dirX / len) * tuning.playerProjectileSpeed;
	const vy = (dirY / len) * tuning.playerProjectileSpeed;
	const bulletSize = tuning.playerProjectileSize;
	const spawnOffset = tuning.playerSize * 0.5 + bulletSize * 0.5 + 2;
	const spawnX = playerTransform.x + (dirX / len) * spawnOffset;
	const spawnY = playerTransform.y + (dirY / len) * spawnOffset;

	spawnProjectile(world, transforms, velocities, projectiles, graphics, stage, spawnX, spawnY, vx, vy, {
		fromEnemy: false,
		lifetime: tuning.playerProjectileLifetime,
		size: bulletSize,
		fill: options.playerProjectileColor,
		stroke: options.playerProjectileStroke
	});

	ammo.remaining = Math.max(0, ammo.remaining - 1);
	if (ammo.remaining <= 0) {
		ammo.reloadRemaining = Math.max(0.05, ammo.reloadDuration);
	}
	applyTimeBoost(world, tuning.timeBoostFireValue, tuning.timeBoostDuration);
}

function findNearestVisibleEnemy(
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	camera: CameraResource,
	bounds: StageBounds,
	origin: TransformComponent
): number | null {
	let bestEntity: number | null = null;
	let bestDist = Number.POSITIVE_INFINITY;
	enemies.forEach((_enemy, entity) => {
		const t = transforms.get(entity);
		if (!t) return;
		const inView = t.x >= 0 && t.x <= bounds.width && t.y >= camera.y && t.y <= camera.y + bounds.height;
		if (!inView) return;
		const dx = t.x - origin.x;
		const dy = t.y - origin.y;
		const d2 = dx * dx + dy * dy;
		if (d2 < bestDist) {
			bestDist = d2;
			bestEntity = entity;
		}
	});
	return bestEntity;
}

function computeInterceptAim(
	shooterX: number,
	shooterY: number,
	targetX: number,
	targetY: number,
	targetVx: number,
	targetVy: number,
	projectileSpeed: number,
	maxTime: number
) {
	// Solve |(target + v*t) - shooter| = s*t
	const rx = targetX - shooterX;
	const ry = targetY - shooterY;
	const vv = targetVx * targetVx + targetVy * targetVy;
	const s2 = projectileSpeed * projectileSpeed;
	const a = vv - s2;
	const b = 2 * (rx * targetVx + ry * targetVy);
	const c = rx * rx + ry * ry;

	let t = 0;
	const eps = 1e-6;
	if (Math.abs(a) <= eps) {
		// Linear: b*t + c = 0
		if (Math.abs(b) > eps) {
			t = -c / b;
		} else {
			t = 0;
		}
	} else {
		const disc = b * b - 4 * a * c;
		if (disc >= 0) {
			const sqrt = Math.sqrt(disc);
			const t1 = (-b - sqrt) / (2 * a);
			const t2 = (-b + sqrt) / (2 * a);
			t = Math.min(t1, t2);
			if (t < 0) t = Math.max(t1, t2);
		} else {
			t = 0;
		}
	}

	if (!Number.isFinite(t) || t <= 0) {
		return { x: targetX, y: targetY };
	}
	t = Math.min(t, maxTime);
	return { x: targetX + targetVx * t, y: targetY + targetVy * t };
}


